import { adminApi, publicRequest, tokenStore } from '@/lib/api';
import type {
  AdminActivityLogEntry,
  AdminAuditTrailEntry,
  AdminSystemSetting,
  AdminTosVersion,
  AdminUser,
  AdminUserStatus,
  AuthTokens,
  CaseLogAudience,
  CaseLogEntry,
  CaseLogEventType,
  VerificationCase,
  VerificationEscalateReason,
  VerificationSubtask,
  WorkflowRole,
  CaseRoleAssignment,
} from '@/types/api';

export type VerificationCaseAction = 'approve' | 'return' | 'reject' | 'request-review';

/**
 * A row in the admin verifications queue (`GET /admin/verifications/`). The list
 * endpoint serializes a flat, user-joined shape — distinct from the rich
 * {@link VerificationCase} returned by the detail endpoint. It carries no
 * `display_id`/`user_display_id`; the user is keyed by `name`/`username`/`email`.
 *
 * ⚠️ `id` is the VerificationRequest id — a DIFFERENT sequence from case ids.
 * Case detail routes must use `case_id`.
 */
export interface VerificationCaseListItem {
  id: number;
  /** Owning VerificationCase id — the only valid input for the case-detail route. */
  case_id?: number | null;
  user_id: number;
  username?: string;
  email?: string;
  /** Candidate phone (plain/searchable columns only; '' when unavailable). */
  phone?: string;
  name?: string;
  /** Workflow admin the case is assigned to (null = unassigned). */
  assignee_id?: number | null;
  assignee_name?: string | null;
  assignee_email?: string | null;
  type: string;
  status: string;
  /** Case creation / submission time. */
  submitted_at?: string | null;
  reviewed_at?: string | null;
  notes?: string | null;
}

/** Account-type filter for the user database. `all` means "no filter". */
export type AdminAccountType = 'all' | 'candidate' | 'employer';

/** A row in the admin user database. Mirrors the legacy `/admin/users/` shape. */
export interface AdminUserListItem {
  id: number;
  user_display_id?: string;
  name?: string;
  username?: string;
  email?: string;
  account_type?: string;
  is_active?: boolean;
  date_joined?: string;
}

/** DRF-paginated page of users; `next`/`previous` drive pagination. */
export interface AdminUserPage {
  results: AdminUserListItem[];
  count: number;
  next: string | null;
  previous: string | null;
}

/** Core account detail for a single user (`GET /admin/users/:id/`). */
export interface AdminUserDetail {
  id: number;
  email?: string;
  name?: string;
  account_type?: string;
  user_display_id?: string;
  is_active?: boolean;
  date_joined?: string;
  last_login?: string | null;
}

/**
 * `GET /admin/users/<id>/` nests the account under `user`, names the account
 * type `profile_type`, and keeps the public id on `account_profile`/`profile`.
 * Older responses were flat, so top-level fields still win when present.
 */
interface RawAdminUserDetail extends Partial<AdminUserDetail> {
  user?: Partial<AdminUserDetail> & { username?: string };
  profile_type?: string;
  account_profile?: { user_display_id?: string } | null;
  profile?: { public_user_id?: string } | null;
}

export function normalizeUserDetail(raw: RawAdminUserDetail): AdminUserDetail {
  const u = raw.user ?? {};
  return {
    id: (raw.id ?? u.id) as number,
    email: raw.email ?? u.email,
    name: raw.name ?? u.name,
    account_type: raw.account_type ?? raw.profile_type,
    user_display_id:
      raw.user_display_id ??
      raw.account_profile?.user_display_id ??
      raw.profile?.public_user_id ??
      undefined,
    is_active: raw.is_active ?? u.is_active,
    date_joined: raw.date_joined ?? u.date_joined,
    last_login: raw.last_login ?? u.last_login,
  };
}

/* -- Candidate / employer profile detail (subset of the legacy shapes) ------ */

export interface AdminAccountProfile {
  public_user_id?: string;
  headline?: string;
  bio?: string;
  location?: string;
  expected_salary?: string | number | null;
  currency?: string;
  period?: string;
  candidate_badge?: string;
  status?: string;
}

export interface AdminPersonalInfo {
  title?: string;
  middle_name?: string;
  given_name?: string;
  family_name?: string;
  family_name_suffix?: string;
  gender?: string;
  nationality?: string;
  birthday?: string | null;
}

export interface AdminContactInfo {
  location_country?: string;
  location_state?: string;
  location_city?: string;
  residence_country?: string;
  residence_province?: string;
  residence_city?: string;
  residence_postal_code?: string;
  residence_street_address?: string;
}

export interface AdminExperienceEntry {
  id: number;
  job_title?: string;
  company_name?: string;
  employment_type?: string;
  work_mode?: string;
  country?: string;
  start_date?: string | null;
  end_date?: string | null;
  currently_working?: boolean;
  verification_status?: string;
}

export interface AdminEducationEntry {
  id: number;
  degree?: string;
  field_of_study?: string;
  institution?: string;
  education_level?: string;
  country?: string;
  start_date?: string | null;
  end_date?: string | null;
  currently_studying?: boolean;
  verification_status?: string;
}

export interface AdminLicenseEntry {
  id: number;
  license_name?: string;
  issued_by?: string;
  type?: string;
  issue_date?: string | null;
  expiry_date?: string | null;
  is_permanent?: boolean;
  verification_status?: string;
}

export interface AdminPhoneEntry {
  phone_type?: string;
  country_code?: string;
  number?: string;
  is_primary?: boolean;
  is_verified?: boolean;
}

export interface AdminEmailEntry {
  email_type?: string;
  email?: string;
  is_primary?: boolean;
  is_verified?: boolean;
}

export interface AdminSalaryEntry {
  id: number;
  job_title?: string;
  base_salary?: string;
  currency?: string;
  salary_frequency?: string;
  expected_salary?: string;
  expected_currency?: string;
  expected_period?: string;
  is_verified?: boolean;
}

/* -- Moderation records, shared by candidates and employers ----------------- */

export interface AdminFlagEntry {
  id: number;
  flag_type?: string;
  reason?: string;
  created_at?: string;
}

export interface AdminNoteEntry {
  id: number;
  note?: string;
  created_at?: string;
}

export interface AdminSuspensionEntry {
  id: number;
  is_active?: boolean;
  reason?: string;
  suspension_type?: string;
  created_at?: string;
}

export interface AdminModeration {
  flags?: AdminFlagEntry[];
  admin_notes?: AdminNoteEntry[];
  suspensions?: AdminSuspensionEntry[];
}

export interface AdminIdVerification {
  id_type?: string;
  country_of_issue?: string;
  id_number?: string;
  issue_date?: string | null;
  expiry_date?: string | null;
  status?: string;
  is_verified?: boolean;
}

export interface AdminResumeDocument {
  id: number;
  file_name?: string;
  file_url?: string | null;
  is_primary?: boolean;
}

export interface AdminSupportingDocument {
  s3_key?: string;
  file_url?: string | null;
}

export interface AdminCandidateDetail extends AdminModeration {
  profile_type?: 'candidate';
  account_profile?: AdminAccountProfile | null;
  personal_info?: AdminPersonalInfo | null;
  contact_info?: AdminContactInfo | null;
  experience_entries?: AdminExperienceEntry[];
  education_entries?: AdminEducationEntry[];
  license_entries?: AdminLicenseEntry[];
  phone_numbers?: AdminPhoneEntry[];
  email_addresses?: AdminEmailEntry[];
  salary_entries?: AdminSalaryEntry[];
  id_verification?: AdminIdVerification | null;
  resume_documents?: AdminResumeDocument[];
}

export interface AdminEmployerProfile {
  company_name?: string;
  position?: string;
  position_rank?: string;
  website?: string;
  industry?: string;
  size?: string;
  verification_case_outcome?: string;
  public_user_id?: string;
}

export interface AdminEmployerDetail extends AdminModeration {
  profile_type?: 'employer';
  employer_profile?: AdminEmployerProfile | null;
  supporting_documents?: AdminSupportingDocument[];
}

/**
 * A row in the workflow admin's completed-case history
 * (`GET /admin/dashboard/completed-case-history/`). Unlike the flat
 * {@link VerificationCaseListItem}, this is built from `VerificationCase` with a
 * resolved `user_display_id` (the `TL-S…`/`TL-E…` badge) and `user_email`. The
 * endpoint is scoped to the **current admin's** assigned cases.
 */
export interface CompletedCaseHistoryItem {
  caseId: number;
  user: number;
  /** Candidate's full name (falls back to the username server-side). */
  user_name?: string;
  userName?: string;
  user_email?: string;
  user_display_id?: string;
  assignee_name?: string;
  type: string;
  verification_type?: string;
  status: string;
  outcome?: string;
  submitted_at?: string | null;
  completed_at?: string | null;
}

/** Custom page envelope returned by the completed-case-history endpoint. */
export interface CompletedCaseHistoryPage {
  count: number;
  page: number;
  page_size: number;
  next_page: number | null;
  previous_page: number | null;
  results: CompletedCaseHistoryItem[];
}

export const adminApiClient = {
  login: (payload: { username: string; password: string }) =>
    // The backend serializes the admin account under `user` (not `admin`) and
    // signals MFA with `requires_mfa`. Keep these key names in sync with
    // applyAdminLogin() below.
    publicRequest<{ access: string; refresh: string; user: AdminUser; requires_mfa?: boolean }>(
      '/admin/auth/login/',
      { method: 'POST', data: payload },
    ),

  verifyMfa: (payload: { otp: string; ticket: string }) =>
    publicRequest<{ access: string; refresh: string; user: AdminUser }>(
      '/admin/auth/mfa/verify/',
      { method: 'POST', data: payload },
    ),

  me: () => adminApi.get<AdminUser>('/admin/auth/me/').then((r) => r.data),

  logout: async () => {
    try {
      await adminApi.post('/admin/auth/logout/');
    } finally {
      tokenStore.setAdmin(null);
    }
  },

  dashboardStats: () =>
    adminApi
      .get<{
        pending_cases: number;
        in_progress_cases: number;
        completed_today: number;
        active_users: number;
      }>('/admin/dashboard/stats/')
      .then((r) => r.data),

  verificationCases: (params: {
    status?: string;
    page?: number;
    assignee_id?: number;
    /** Free-text: name / email / phone / case number. Server-side. */
    search?: string;
  }) =>
    adminApi
      .get<{ results: VerificationCaseListItem[]; count: number }>('/admin/verifications/', {
        params,
      })
      .then((r) => r.data),

  verificationCase: (id: number | string) =>
    adminApi.get(`/admin/verifications/${id}/`).then((r) => {
      // The detail endpoint wraps the case under `verification` and uses legacy
      // key names (case_type / lifecycleStatus, subtask.taskId). Normalize it to
      // the flat {@link VerificationCase} contract the UI consumes.
      interface LegacyCasePayload extends Partial<Omit<VerificationCase, 'subtasks'>> {
        case_type?: string;
        verification_type?: string;
        lifecycleStatus?: string;
        submitted_at?: string;
        review_returned_at?: string;
        account_profile?: { user_display_id?: string };
        subtasks?: Array<VerificationSubtask & { taskId?: number }>;
        assignee_id?: number | null;
        assignee_name?: string | null;
      }
      const raw: LegacyCasePayload = r.data?.verification ?? r.data ?? {};
      const subtasks = (raw.subtasks ?? []).map((t) => ({
        ...t,
        id: t.id ?? t.taskId,
      }));
      const normalized: VerificationCase = {
        ...(raw as object),
        // Legacy payloads always carry an id; the fallback chains below only
        // guard the optional/renamed fields.
        id: raw.id as number,
        type: raw.case_type ?? raw.verification_type ?? raw.type ?? '',
        status: raw.lifecycleStatus ?? raw.status ?? '',
        outcome: raw.outcome ?? '',
        display_id: raw.display_id ?? (raw.id != null ? `VC-${raw.id}` : ''),
        user_display_id:
          raw.user_display_id ?? raw.account_profile?.user_display_id ?? '',
        created_at: raw.created_at ?? raw.submitted_at ?? '',
        updated_at: raw.updated_at ?? raw.review_returned_at ?? raw.submitted_at ?? '',
        assigneeAdminId: raw.assigneeAdminId ?? raw.assignee_id ?? null,
        assigneeName: raw.assigneeName ?? raw.assignee_name ?? null,
        subtasks,
      };
      return normalized;
    }),

  /**
   * The current admin's completed-case history — the only verifications source
   * that carries a resolved `user_display_id`. Paginated (page_size max 100).
   */
  completedCaseHistory: (params: { page?: number; page_size?: number } = {}) =>
    adminApi
      .get<CompletedCaseHistoryPage>('/admin/dashboard/completed-case-history/', { params })
      .then((r) => r.data),

  /**
   * Case outcome controls: approve / return / reject / request-review.
   * return and request-review are PATCH server-side; approve and reject are POST.
   */
  verificationAction: (id: number | string, action: VerificationCaseAction) => {
    const url = `/admin/verifications/${id}/${action}/`;
    const isPatch = action === 'return' || action === 'request-review';
    return (isPatch ? adminApi.patch(url) : adminApi.post(url)).then((r) => r.data);
  },

  verificationSubtaskUpdate: (
    taskId: number | string,
    payload: { status?: string; outcome?: string },
  ) =>
    adminApi.patch(`/admin/verification-subtasks/${taskId}/update/`, payload).then((r) => r.data),

  /**
   * WS-5: return a subtask to the candidate for resubmission with a reason and
   * required-evidence checklist (e.g. institution rename proof). Only the case
   * Maker (assignee) is authorised server-side.
   */
  verificationSubtaskRequestResubmit: (
    taskId: number | string,
    payload: {
      reason: string;
      required_evidence?: string[];
      name_change?: Record<string, unknown> | null;
    },
  ) =>
    adminApi
      .post(`/admin/verification-subtasks/${taskId}/request-resubmit/`, payload)
      .then((r) => r.data),

  /** WS-3: read a case timeline, optionally filtered to one audience. */
  caseLog: (id: number | string, audience?: CaseLogAudience) =>
    adminApi
      .get<CaseLogEntry[]>(`/admin/verification-cases/${id}/log/`, {
        params: audience ? { audience } : undefined,
      })
      .then((r) => r.data),

  /**
   * WS-3: post a case/subtask comment. `audience: 'external'` is candidate-facing
   * and is only accepted from the case Maker (server-enforced); default internal.
   */
  postCaseComment: (
    id: number | string,
    payload: {
      body: string;
      audience?: CaseLogAudience;
      event_type?: CaseLogEventType;
      subtask_id?: number | null;
    },
  ) =>
    adminApi
      .post<CaseLogEntry>(`/admin/verification-cases/${id}/log/`, payload)
      .then((r) => r.data),

  verificationReassign: (caseId: number | string, assigneeId: number) =>
    adminApi
      .post(`/admin/verification-cases/${caseId}/reassign/`, { assignee_id: assigneeId })
      .then((r) => r.data),

  /**
   * Hand the *review* of a case to a different Checker/Supervisor/Super Admin.
   * Same endpoint as `verificationReassign`, which switches on which id it gets;
   * sending both is rejected. Valid until the case reaches a verdict.
   */
  verificationReassignReviewer: (caseId: number | string, reviewerId: number) =>
    adminApi
      .post(`/admin/verification-cases/${caseId}/reassign/`, { reviewer_id: reviewerId })
      .then((r) => r.data),

  /** WS-1: give an admin a workflow role on a case (Supervisor/Super-Admin only). */
  verificationAssignRole: (
    caseId: number | string,
    payload: { admin_id: number; role: WorkflowRole },
  ) =>
    adminApi
      .post(`/admin/verification-cases/${caseId}/assign-role/`, payload)
      .then((r) => r.data),

  /**
   * WS-1: the per-case role overrides set on a case. An override outranks the
   * admin's account-level `workflow_role` server-side, so this read is what makes
   * one visible instead of a silent change in what someone may do.
   */
  verificationCaseRoles: (caseId: number | string) =>
    adminApi
      .get<CaseRoleAssignment[]>(`/admin/verification-cases/${caseId}/roles/`)
      .then((r) => r.data),

  /** WS-1: drop an override so the admin falls back to their account role. */
  verificationRevokeRole: (caseId: number | string, adminId: number) =>
    adminApi
      .post(`/admin/verification-cases/${caseId}/revoke-role/`, { admin_id: adminId })
      .then((r) => r.data),

  /**
   * WS-1: the Maker submits the case for review ("Make" transition).
   * Moves the case to `checking`, with a Checker as reviewer. Server-enforced to
   * the case Maker.
   * NOTE: this endpoint is PATCH (not POST like the other outcome controls).
   */
  verificationRequestReview: (id: number | string, payload: { suggested_status?: string } = {}) =>
    adminApi.patch(`/admin/verifications/${id}/request-review/`, payload).then((r) => r.data),

  /** Approve a case. `admin_notes` is recorded on each verification request. */
  verificationApprove: (id: number | string, payload: { admin_notes?: string } = {}) =>
    adminApi.post(`/admin/verifications/${id}/approve/`, payload).then((r) => r.data),

  /**
   * Return a case to the workflow admin with reviewer comments.
   * NOTE: this endpoint is PATCH, like request-review.
   */
  verificationReturn: (id: number | string, payload: { return_comments?: string } = {}) =>
    adminApi.patch(`/admin/verifications/${id}/return/`, payload).then((r) => r.data),

  /** Reject a case. `flag_user` optionally raises a UserFlag. */
  verificationReject: (
    id: number | string,
    payload: { reason: string; explanation?: string; flag_user?: boolean },
  ) => adminApi.post(`/admin/verifications/${id}/reject/`, payload).then((r) => r.data),

  /** Request more information from the user; `message` is sent as a notification. */
  verificationRequestInfo: (id: number | string, payload: { message: string }) =>
    adminApi.post(`/admin/verifications/${id}/request-info/`, payload).then((r) => r.data),

  /** Escalate the case to a senior admin queue. */
  verificationEscalate: (
    id: number | string,
    payload: { reason: VerificationEscalateReason; notes?: string },
  ) => adminApi.post(`/admin/verifications/${id}/escalate/`, payload).then((r) => r.data),

  /** Resend the verification email to the user (no payload). */
  verificationResendEmail: (id: number | string) =>
    adminApi.post(`/admin/verifications/${id}/resend-email/`).then((r) => r.data),

  /** Assign the case to a specific staff admin. */
  verificationAssign: (id: number | string, adminId: number) =>
    adminApi.post(`/admin/verifications/${id}/assign/`, { admin_id: adminId }).then((r) => r.data),

  /** Create a new subtask on a case. */
  verificationSubtaskCreate: (payload: {
    case: number;
    type: string;
    contents?: Record<string, unknown>;
  }) =>
    adminApi
      .post<VerificationSubtask>('/admin/verification-subtasks/', payload)
      .then((r) => r.data),

  /** Request a presigned PUT URL for an admin supporting document upload. */
  verificationSubtaskPresign: (
    taskId: number | string,
    payload: { file_name: string; content_type: string },
  ) =>
    adminApi
      .post<{ url: string }>(`/admin/verification-subtasks/${taskId}/presigned-url/`, {
        id: Number(taskId),
        ...payload,
      })
      .then((r) => r.data),

  /** Delete an admin supporting document from a subtask by its S3 key. */
  verificationSubtaskDocumentDelete: (taskId: number | string, s3Key: string) =>
    adminApi
      .delete(`/admin/verification-subtasks/${taskId}/documents`, { params: { s3_key: s3Key } })
      .then((r) => r.data),

  users: (params: { q?: string; account_type?: AdminAccountType; page?: number }) => {
    // The backend `users_list` is a POST search reading a JSON body (`UserSearchSerializer`):
    // the search term is `query` (not `q`), and `all` is a valid no-filter value. It caps at
    // 50 rows with no DRF pagination, so `page` has no effect server-side.
    const body = { query: params.q ?? '', account_type: params.account_type ?? 'all' };
    return adminApi.post<AdminUserPage>('/admin/users/', body).then((r) => r.data);
  },

  userDetail: (id: number | string) =>
    adminApi.get<RawAdminUserDetail>(`/admin/users/${id}/`).then((r) => normalizeUserDetail(r.data)),

  candidateDetail: (id: number | string) =>
    adminApi.get<AdminCandidateDetail>(`/admin/candidate-detail/${id}/`).then((r) => r.data),

  employerDetail: (id: number | string) =>
    adminApi.get<AdminEmployerDetail>(`/admin/employer-detail/${id}/`).then((r) => r.data),

  auditLogs: (params: { page?: number; category?: string }) =>
    adminApi
      .get<{ results: unknown[]; count: number }>('/admin/logs/audit/', { params })
      .then((r) => r.data),

  /**
   * Content reports. The backend route is `/admin/moderation/reports/` and it
   * returns a plain array of MessageReport rows; `/admin/reports/` never
   * existed, so the page always showed "No reports" (#146).
   */
  reports: (params: { status?: string }) =>
    adminApi
      .get<RawMessageReport[]>('/admin/moderation/reports/', { params })
      .then((r) => {
        const results = (Array.isArray(r.data) ? r.data : []).map(toAdminReport);
        return { results, count: results.length };
      }),
};

/** A content report (`/admin/reports/`). */
export interface AdminReport {
  id: number;
  type?: string;
  reported_by?: string;
  target?: string;
  status?: string;
  created_at?: string;
}

/** A row of `GET /admin/moderation/reports/` (messaging MessageReportSerializer). */
export interface RawMessageReport {
  id: number;
  thread?: number | null;
  reporter?: number | null;
  reporter_name?: string | null;
  reported_user?: number | null;
  reported_user_name?: string | null;
  reason?: string | null;
  status?: string | null;
  reported_at?: string | null;
}

export function toAdminReport(row: RawMessageReport): AdminReport {
  const reporter = row.reporter_name?.trim() || (row.reporter != null ? `User #${row.reporter}` : undefined);
  const reported = row.reported_user_name?.trim() || (row.reported_user != null ? `User #${row.reported_user}` : undefined);
  return {
    id: row.id,
    type: row.reason ? row.reason.replace(/_/g, ' ') : undefined,
    reported_by: reporter,
    target: reported ? (row.thread != null ? `${reported} · conversation #${row.thread}` : reported) : undefined,
    status: row.status ? row.status.replace(/_/g, ' ') : undefined,
    created_at: row.reported_at ?? undefined,
  };
}

/**
 * An admin account as returned by `/admin/list/`. The endpoint keys the display
 * name as `name` (= the admin's username) and exposes the privilege as a `role`
 * string ('Super Admin' | 'Workflow Admin') rather than an `is_superuser` flag.
 */
/**
 * Account-level workflow role for the WS-1 Maker-Checker-Supervisor pipeline.
 * Extends the per-case {@link WorkflowRole} with '' meaning "no account default"
 * (per-case assignments still apply).
 */
export type AccountWorkflowRole = WorkflowRole | '';

export interface AdminManagedUser {
  id: number;
  name: string;
  email: string;
  role: string;
  status?: string;
  module_access: Record<string, boolean>;
  workflow_role: AccountWorkflowRole;
}

export interface AdminUserInput {
  username: string;
  email: string;
  is_superuser: boolean;
  module_access: Record<string, boolean>;
  workflow_role: AccountWorkflowRole;
}

export const adminManagementApi = {
  list: () => adminApi.get<AdminManagedUser[]>('/admin/list/').then((r) => r.data),

  create: (payload: AdminUserInput & { password: string }) =>
    adminApi.post('/admin/create/', { ...payload, is_staff: true }).then((r) => r.data),

  update: (id: number, payload: AdminUserInput) =>
    adminApi.put(`/admin/${id}/update/`, payload).then((r) => r.data),
};

/** Write actions against a single user account (`/admin/users/<id>/*`). */
export const adminUserActionsApi = {
  /** Change account status. `suspended`/`banned` create a UserSuspension. */
  updateStatus: (id: number | string, payload: { status: AdminUserStatus; reason?: string }) =>
    adminApi.post(`/admin/users/${id}/status/`, payload).then((r) => r.data),

  /** Email the user the standard forgot-password code; 502 when sending fails. */
  resetPassword: (id: number | string) =>
    adminApi
      .post<{ message?: string }>(`/admin/users/${id}/reset-password/`)
      .then((r) => r.data),

  /** Soft-delete the account (7-day grace period). */
  remove: (id: number | string) =>
    adminApi.delete(`/admin/users/${id}/delete/`).then((r) => r.data),

  /** Raise a moderation flag on the account. */
  flag: (id: number | string, payload: { reason: string; description?: string }) =>
    adminApi.post(`/admin/users/${id}/flag/`, payload).then((r) => r.data),

  /** Attach an internal admin note to the account. */
  addNote: (id: number | string, payload: { note: string; is_important?: boolean }) =>
    adminApi.post(`/admin/users/${id}/notes/`, payload).then((r) => r.data),

  /** Recent activity log for the user. */
  activityLog: (id: number | string) =>
    adminApi
      .get<AdminActivityLogEntry[]>(`/admin/users/${id}/activity-log/`)
      .then((r) => r.data),

  /** Tamper-evident audit trail for the user. */
  auditTrail: (id: number | string) =>
    adminApi.get<AdminAuditTrailEntry[]>(`/admin/users/${id}/audit-trail/`).then((r) => r.data),

  /** Server-side export of selected users (returns a download link). */
  export: (payload: { user_ids: number[]; format?: 'excel' | 'pdf' }) =>
    adminApi
      .post<{ message: string; download_url: string; expires_at: string }>(
        '/admin/users/export/',
        payload,
      )
      .then((r) => r.data),
};

/** Platform configuration mutations (`/admin/settings/*`). */
export const adminSettingsApi = {
  list: () => adminApi.get<AdminSystemSetting[]>('/admin/settings/').then((r) => r.data),

  /** Upsert a single setting by key. */
  update: (payload: {
    setting_key: string;
    setting_value: string;
    description?: string;
    setting_type?: string;
  }) => adminApi.post<AdminSystemSetting>('/admin/settings/update/', payload).then((r) => r.data),

  /** Toggle a named feature flag (stored as `feature_<name>`). */
  toggleFeature: (payload: { feature: string; enabled: boolean }) =>
    adminApi
      .post<{ message: string; feature: string; enabled: boolean }>(
        '/admin/settings/toggle-feature/',
        payload,
      )
      .then((r) => r.data),

  /** Upload a new Terms-of-Service version. */
  uploadTos: (payload: {
    version: string;
    version_type?: string;
    content: string;
    requires_re_signature?: boolean;
  }) =>
    adminApi.post<AdminTosVersion>('/admin/settings/tos/upload/', payload).then((r) => r.data),

  /** Full ToS version history, newest first. */
  tosHistory: () =>
    adminApi.get<AdminTosVersion[]>('/admin/settings/tos/history/').then((r) => r.data),
};

export function applyAdminLogin(response: {
  access: string;
  refresh: string;
  // Backend returns the admin account under `user`; tolerate the legacy `admin`
  // key too so a future backend change can't silently break login.
  user?: AdminUser;
  admin?: AdminUser;
}): {
  admin: AdminUser;
  tokens: AuthTokens;
} {
  const tokens: AuthTokens = { access: response.access, refresh: response.refresh };
  const admin = response.user ?? response.admin;
  if (!admin) {
    // Fail loudly rather than persisting `undefined` — an undefined admin would
    // pass the in-memory guard but never survive a reload (the logout-on-refresh bug).
    throw new Error('Admin login response did not include an admin account.');
  }
  tokenStore.setAdmin(tokens);
  return { admin, tokens };
}
